import type {KeyTextField} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import {PrismicNextImage} from '@prismicio/next/pages'
import {PrismicRichText} from '@prismicio/react'
import Link from 'next/link'
import {useLinkResolver} from 'prismicio'
import type {CustomerStoryDocument, IndustryDocument} from 'prismicio-types'
import {memo} from 'react'
import {Icon} from 'src/common/Icon'
import {EVENTS, track} from 'src/common/helpers'

type LinkResolverObject = {
  pathname: string
  query: {
    [k: string]: string
  }
}

const getUrl = (href: LinkResolverObject) => {
  const queryString = new URLSearchParams(href.query).toString()
  return `${href.pathname}${queryString ? `?${queryString}` : ''}`
}

export const CustomerStoryCard = memo(function (props: {
  customerStory: CustomerStoryDocument
  readMoreLabel: KeyTextField
  index: number
  pageUid: string
}) {
  const {linkResolver} = useLinkResolver()
  const link = linkResolver(props.customerStory)
  const url = getUrl(link)

  const industry = props.customerStory.data.industry

  const industryName = isFilled.contentRelationship(industry) ? (industry.data as IndustryDocument['data'])?.name : null

  return (
    <Link
      className="group/card col-span-full flex flex-col gap-4 rounded-lg bg-moss-white p-4 active:bg-beige-300 md:col-span-6 md:gap-6 md:p-6 lg:col-span-4"
      href={url}
      locale={props.customerStory.lang || 'en-us'}
      onClick={() =>
        track({
          event: EVENTS.CLICK,
          mktgId: `customer-story-read-more-button`,
          location: `component-${props.index}-customer-story`,
          pageName: props.pageUid,
          dataType: 'customer-story-button',
        })
      }
    >
      <div className="relative h-[200px] w-full overflow-hidden rounded-[4px]">
        <PrismicNextImage className="size-full object-cover" field={props.customerStory.data.image} priority />
        <div className="absolute inset-x-0 bottom-0 h-[43%] bg-gradient-to-t from-[#002414]/80 to-transparent" />
        {isFilled.image(props.customerStory.data.company_logo) && (
          <div className="absolute bottom-3 left-3 z-10 h-7 w-[108px] transition-transform duration-200 group-hover/card:translate-y-[-10px]">
            <PrismicNextImage
              field={props.customerStory.data.company_logo}
              fill
              className="object-contain object-left"
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        {props.customerStory.data.company_name && (
          <span className="text-16 font-medium leading-5 text-black/[60%]">
            {props.customerStory.data.company_name}
          </span>
        )}
        {industryName && (
          <span className="text-button-s rounded-[4px] bg-beige-200 px-2 py-1 text-moss-black/[60%]">
            {industryName}
          </span>
        )}
      </div>

      <PrismicRichText
        field={props.customerStory.data.title}
        components={{
          heading1: ({children}) => (
            <span className="truncate-3-lines text-24 leading-[30px] text-black">{children}</span>
          ),
        }}
      />

      {isFilled.keyText(props.readMoreLabel) && (
        <div className="relative mt-auto flex h-[38px] w-fit items-center gap-[14px] rounded-[4px] border border-moss-grey-150 bg-moss-white pl-[14px] pr-[7px] transition-colors duration-100 group-hover/card:bg-moss-grey-100">
          <span className="text-button-s uppercase text-black">{props.readMoreLabel}</span>
          <div className="flex size-6 items-center justify-center rounded-sm bg-green-neon group-hover/card:bg-green-neon/[80%]">
            <Icon name="arrow" className="h-2 w-2.5 text-green-dark" />
          </div>
        </div>
      )}
    </Link>
  )
})
